import React, { useState } from "react";
import ColorTokensExample from "../../components/Shared/ColorTokens/ColorTokens.example";
import AppInputNewExample from "../../components/Core/AppInput/AppInputNew.example";
import AppSelectExample from "../../components/Core/AppSelect/AppSelect.example";
import AppAlertDialogExample from "../../components/Core/AppAlertDialog/AppAlertDialog.example";
import AlertExample from "../../components/Core/Alert/Alert.example";
import TextAreaNewExample from "../../components/Core/TextArea/TextAreaNew.example";
import RatingExample from "../../components/Core/Rating/Rating.example";
import SearchableCheckboxListExample from "../../components/Core/SearchableCheckboxList/SearchableCheckboxList.example";
import CheckboxNewExample from "../../components/Core/Checkbox/CheckboxNew.example";
import RadioGroupExample from "../../components/Core/RadioGroup/RadioGroup.example";
import DayPickerNewExample from "../../components/Core/DayPicker/DayPickerNew.example";
import ButtonNewExample from "../../components/Core/Button/ButtonNew.example";
import SplitButtonNewExample from "../../components/Core/Button/SplitButtonNew.example";
import OBMatrixExample from "../../components/Shared/OBMatrix/OBMatrix.example";
import SheetExample from "../../components/ui/sheet.example";
import FloatingPanelExample from "../../components/Core/FloatingPanel/FloatingPanel.example";
import AppNavTreeExample from "../../components/Core/AppNavTree/AppNavTree.example";
import SortableThExample from "../../components/Core/SortableTh/SortableTh.example";
import SegmentedControlExample from "../../components/Core/SegmentedControl/SegmentedControl.example";
import FormCardExample from "../../components/Core/FormCard/FormCard.example";
import CustomFieldsSectionExample from "../../components/Shared/CustomFields/CustomFieldsSection.example";
import TabsExample from "../../components/ui/tabs.example";
import DropdownMenuExample from "../../components/ui/dropdown-menu.example";
import CheckboxExample from "../../components/ui/checkbox.example";
import RouteTabsExample from "../../components/Core/RouteTabs/RouteTabs.example";
import DetailPageTabsExample from "../../components/Shared/DetailPageTabs/DetailPageTabs.example";
import AddButtonExample from "../../components/Core/AddButton/AddButton.example";
import OutlineButtonExample from "../../components/Core/OutlineButton/OutlineButton.example";
import ConsultantHeaderExample from "../../components/Shared/ConsultantHeader/ConsultantHeader.example";
import CustomerHeaderExample from "../../components/Shared/CustomerHeader/CustomerHeader.example";
import TagSelectPopoverExample from "../../components/Shared/TagSelectPopover/TagSelectPopover.example";
import CertificationSelectPopoverExample from "../../components/Shared/CertificationSelectPopover/CertificationSelectPopover.example";
import ContactPersonExample from "../../components/Shared/ContactPerson/ContactPerson.example";
import BreadcrumbsExample from "../../components/Shared/Breadcrumbs/Breadcrumbs.example";
import ShiftPlannerExample from "../../components/Shared/ShiftPlanner/ShiftPlanner.example";
import EventTimelineExample from "../../components/Shared/EventTimeline/EventTimeline.example";
import AiAssistantExample from "../../components/Shared/AiAssistant/AiAssistant.example";

interface ComponentExample {
	name: string;
	component: React.ComponentType;
	description?: string;
}

const componentExamples: ComponentExample[] = [
	{
		name: "AiAssistant",
		component: AiAssistantExample,
		description:
			"The chat pieces behind the docked AI-assistent sidebar (⌘J): AiMessage renders user bubbles and assistant markdown (GFM tables, in-app links) with a collapsible tool-call trace; AiApprovalCard is the one-card-per-turn approval step for proposed writes (check rows, Godkänn / Avvisa alla); AiPromptInput is the auto-growing Enter-to-send composer. Static data here — the real sidebar (Shared/AiAssistant/AiAssistantSidebar) talks to oo-integration-service's non-streaming /ai endpoints via networking/ai-assistant.hooks.",
	},
	{
		name: "FloatingPanel",
		component: FloatingPanelExample,
		description:
			"A floating, inline companion panel — a rounded, shadowed card inset from the screen edge (right by default, or left) that surfaces related content beside what you're doing without navigating away (e.g. peek at a job's time reports while staying in the shift planner). Built on Radix Dialog but NON-MODAL by default: no backdrop, doesn't lock or steal clicks from the page behind it, and stays open when you click outside (Escape / the close button dismiss it). Compound API mirroring Sheet — FloatingPanel + Content (side, dismissOnOutsideClick, dismissOnEscape, optional overlay for a modal variant) + Header/Title/Description/Body/Footer. The counterpart to the edge-pinned modal Sheet: reach for FloatingPanel when the page should stay usable, Sheet when the panel should take over.",
	},
	{
		name: "ShiftPlanner",
		component: ShiftPlannerExample,
		description:
			"The consolidated 'Arbetspass & konsulter' week grid for the job details page — consultant rows × weekday columns with green booked pills, a grey 'Välj konsult' row for unassigned shifts, popover time/break editing committed on Enter/'Klar', shift+click or shift+drag multi-select of slots (within a row or across rows) that edits/creates all selected slots in one go, absence + pending application/invitation states. Fully controlled, presentational and grid-only: the example shows just the component with alerts at the parent hook-in points (consultant picker, save); page chrome like summary/week-paging/clear-all belongs to the integrating page. The dashed bar demos the parent-side save lifecycle via the exported useShiftPlannerHistory (gesture-level undo/redo) and diffShifts (live created/updated/removed preview before save).",
	},
	{
		name: "EventTimeline",
		component: EventTimelineExample,
		description:
			"Shared vertical event timeline behind every 'Händelser' tab (job, consultant, customer), shown here in its job flavour — dots on a shared line grouped under date headers, newest first. Renders the unified job history feed (job lifecycle, applications, invitations, shifts, status changes, time report milestones) with actor names, field-level diff rows for updates ('Starttid: 08:00 → 09:00'), shift context on time report events, 'System' for cron events, and graceful label-only fallback for legacy entries.",
	},
	{
		name: "Breadcrumbs",
		component: BreadcrumbsExample,
		description:
			"Top-of-page navigation bar (page title + '/ sub page', optional status pill / pool dropdown). fullBleed by default: it measures its parent's padding and cancels it with negative margins so the bar always sits flush to the top/side edges of the scroll container — regardless of whether the page wraps it in a padded content div. Pass fullBleed={false} to flow inline instead.",
	},
	{
		name: "ConsultantHeader",
		component: ConsultantHeaderExample,
		description:
			"Profile summary header for the consultant detail page — avatar plus a harmonized Tailwind grid of contact info, status, star ratings (Arbetsinsats/Punktlighet), weekly availability and app activity. Next-gen replacement for the inline Chakra/styled header; reuses the Rating, WeeklyAvailability and ProfilePictureReborn primitives.",
	},
	{
		name: "CustomerHeader",
		component: CustomerHeaderExample,
		description:
			"Profile summary header for the customer detail page — avatar plus a harmonized Tailwind grid of contact info, status, contract and metadata. Sibling of ConsultantHeader; both compose the shared DetailHeader field atoms (truncation + on-overflow tooltip + copy-to-clipboard).",
	},
	{
		name: "AddButton",
		component: AddButtonExample,
		description:
			"Dashed-border 'add / create' action button with a leading plus icon — the canonical inline 'add a new X' affordance (e.g. 'Lägg till tagg', 'Skapa ny koncern'). Domain-free; pass a label and onClick, restyle via className.",
	},
	{
		name: "OutlineButton",
		component: OutlineButtonExample,
		description:
			"Solid-bordered secondary action button — white background, gray border, label with an optional trailing icon. The compact counterpart of the dashed AddButton for actions that open a picker/dialog (e.g. 'Välj kontaktperson', 'Ladda upp dokument'). Forwards refs so it works as a Radix asChild trigger.",
	},
	{
		name: "TagSelectPopover",
		component: TagSelectPopoverExample,
		description:
			"Trello-style label picker — a dashed AddButton that opens a popover listing every tag as a colored bar with a checkbox. Checking assigns, unchecking removes (toggle many without closing); a search box filters long taxonomies. Powers the consultant 'Lägg till tagg' flow. Built on the Radix popover primitive (components/ui/popover).",
	},
	{
		name: "CertificationSelectPopover",
		component: CertificationSelectPopoverExample,
		description:
			"Certification counterpart of TagSelectPopover — the same dashed AddButton + searchable checkbox popover, but plain rows (no tag color) with an award icon. Powers the 'Lägg till certifikat' flow in the job requirements editor.",
	},
	{
		name: "ContactPerson",
		component: ContactPersonExample,
		description:
			"The job form's contact person block — free-text person fields (AppInputNew) plus a 'Välj kontaktperson' dropdown (Radix dropdown-menu) that prefills them from the customer's users. Values stay free-text so a picked person can be edited.",
	},
	{
		name: "Color Tokens",
		component: ColorTokensExample,
		description:
			"Canonical brand, semantic and neutral theme tokens — the colors new Tailwind components should use",
	},
	{
		name: "AppInputNew",
		component: AppInputNewExample,
		description: "New Tailwind-based input component with customizable styling",
	},
	{
		name: "AppSelect",
		component: AppSelectExample,
		description: "Tailwind-based select component built on shadcn/ui with React Hook Form support",
	},
	{
		name: "AppAlertDialog",
		component: AppAlertDialogExample,
		description:
			"Reusable confirmation dialog built on the shadcn/Radix alert-dialog primitive — trigger-based or controlled, danger variant, async confirm with loading",
	},
	{
		name: "Alert",
		component: AlertExample,
		description:
			"Inline, non-toast status message (alert/callout) — mandatory coloured title with optional body, a success/warning/error/info variant with matching icon, and optional dismiss (✕) and trailing action. Domain-free; pass i18n keys or literals with skipTranslation. For modal confirmations use AppAlertDialog.",
	},
	{
		name: "TextAreaNew",
		component: TextAreaNewExample,
		description: "New Tailwind-based textarea component with AI generation support",
	},
	{
		name: "CheckboxNew",
		component: CheckboxNewExample,
		description:
			"New Tailwind-based checkbox with a bold label and optional muted hint (subtext) beneath it. Controlled/uncontrolled, disabled and error states, brand-colored box, plus a CheckboxNewController for React Hook Form.",
	},
	{
		name: "Rating",
		component: RatingExample,
		description: "Custom star rating component with interactive and readonly modes",
	},
	{
		name: "RadioGroup",
		component: RadioGroupExample,
		description:
			"Tailwind-based radio button group — horizontal (default) or vertical, brand-colored selected dot, per-option hint/disabled, label/mandatory/error states. Native inputs drive accessibility; includes a RadioGroupController for React Hook Form.",
	},
	{
		name: "ButtonNew",
		component: ButtonNewExample,
		description:
			"Tailwind replacement for the styled-components Button — primary/danger/dark/neutral variants on the theme tokens, fixed h-10 so a row of actions always lines up. Renders an <a> when given href instead of needing an anchor wrapper (wrapping a button in an anchor makes it a different flex item and breaks alignment). Customise via className rather than margin/width/backgroundColor props.",
	},
	{
		name: "SplitButtonNew",
		component: SplitButtonNewExample,
		description:
			'ButtonNew with a chevron segment opening a dropdown of secondary actions — the "Boka 1 pass ⌄" pattern generalised. Same variants and h-10 as ButtonNew; the primary and chevron segments are separate buttons with their own disabled state, and the chevron is left out when there are no items.',
	},
	{
		name: "DayPickerNew",
		component: DayPickerNewExample,
		description:
			"Tailwind/shadcn date field — a bordered trigger showing the formatted date, opening the frost-ui-react calendar inside a Radix popover. Replaces the styled-components DayPicker: because the popover portals, it no longer gets clipped by overflow or stacking contexts. Label/description are i18n keys; customisation is via containerClassName/triggerClassName rather than margin/width props.",
	},
	{
		name: "SearchableCheckboxList",
		component: SearchableCheckboxListExample,
		description:
			"Multi-select checkbox list with a top search box for long option sets (hundreds of customers). Diacritics-insensitive filter; Enter checks the option when the search narrows to a single match. Powers the filter sidebar's Kund/Avtal/Pool sections.",
	},
	{
		name: "OBMatrix",
		component: OBMatrixExample,
		description:
			"Matrix component for adding and editing OB rules with weekdays, time ranges, and supplements",
	},
	{
		name: "Sheet",
		component: SheetExample,
		description:
			"Radix Dialog-based side sheet (drawer/sidebar replacement) — slides from any side, composes with nested dialogs without trapFocus/z-index hacks",
	},
	{
		name: "AppNavTree",
		component: AppNavTreeExample,
		description:
			"Searchable, VS Code settings-style navigation tree — recursive expand/collapse nodes, leaf or branch selection, with a built-in filter that auto-expands and highlights matches",
	},
	{
		name: "SortableTh",
		component: SortableThExample,
		description:
			"Canonical sort-aware table header cell — generic over the column enum, renders the standard list-table header styling with a SortIcon reflecting the active order. Shared by the jobs/customers/consultants virtualized list pages.",
	},
	{
		name: "SegmentedControl",
		component: SegmentedControlExample,
		description:
			"Single-select segmented control (button group) — the flat, space-efficient alternative to RadioGroup for a short mutually-exclusive choice. Controlled/uncontrolled, sm/md sizes, full-width, disabled and error states, action-colored selected segment, plus a SegmentedControlController for React Hook Form. Powers the customer Kundtyp selector.",
	},
	{
		name: "FormCard",
		component: FormCardExample,
		description:
			"Titled section card for grouping related form fields — uppercase muted heading with an optional right-aligned action over a bordered, padded surface. Domain-free; pass an i18n title and the fields as children, restyle via className. Used to structure the customer profile form.",
	},
	{
		name: "CustomFieldsSection",
		component: CustomFieldsSectionExample,
		description:
			"The org-defined custom fields of one host card on the job / customer / consultant forms — one control per definition placed there (text, number, date, Lista select or Ja/Nej checkbox), either as its own 'Övriga uppgifter' FormCard (variant card) or as rows inside an existing card (variant inline). Takes entity + placement + the form's definitions and the host's react-hook-form control/register/errors; renders nothing when no definition is placed on the card. On job forms, inheritable customer fields render against the customer's value: 'show' read-only with an 'Ärvd från kund' hint, 'override' as Ärv | Ja | Nej or an input with the inherited value as placeholder and 'Återställ till ärvd'. On customer forms of a koncern member (corporateGroupMember), group-owned fields render read-only with an 'Ägs av koncern' hint (or 'Sätts av koncernen' while the koncern's values are unknown); ignorePlacement renders every definition regardless of card (the koncern form). Ships helpers: customFieldDefaults / customFieldFormValues, customFieldsSchema (required validation, inherited-aware), buildCustomFieldsPayload (full active map; untouched override keys left out) and buildCustomFieldsPayloadWithStored for tabs that render a subset.",
	},
	{
		name: "DropdownMenu",
		component: DropdownMenuExample,
		description:
			"shadcn/Radix dropdown-menu primitive (components/ui/dropdown-menu) — label, items (with leading lucide icons + right-aligned shortcuts), separators, checkbox items, and a colored-tag submenu. Backs the revamped consultant contact context menu (Shared/ContactContextMenu); the example replicates that menu with mock data.",
	},
	{
		name: "Checkbox (ui)",
		component: CheckboxExample,
		description:
			"shadcn/Radix checkbox primitive (components/ui/checkbox) — an unlabelled box for composing inside other Tailwind components, where the host row owns the label and the hit area (e.g. the list-page column settings rows). For a form field with label/hint/error and react-hook-form support, use Core/Checkbox/CheckboxNew.",
	},
	{
		name: "Tabs",
		component: TabsExample,
		description:
			"shadcn/Radix Tabs primitive (components/ui/tabs) — pure state-based tab control with the default segmented look, customisable via className. The lowest layer the route-aware RouteTabs builds on.",
	},
	{
		name: "RouteTabs",
		component: RouteTabsExample,
		description:
			'Route-aware tab bar built on the shadcn Tabs primitive — the modern replacement for the legacy Shared/Tabs/TabGroup for detail-page sub-navigation (customer/consultant/job tabs). Active tab derived from the URL, selecting a tab navigates, so tabs stay deep-linkable. The filter pills (Shared/Tabs/Tab role="filter") are intentionally left as-is.',
	},
	{
		name: "DetailPageTabs",
		component: DetailPageTabsExample,
		description:
			'The canonical detail-page tab bar — the "styled for this purpose" preset of RouteTabs: a white, bordered, rounded bar with a rounded highlight on the active tab and an optional "..." overflow actions menu. Use this (not raw RouteTabs) on the customer / consultant / job detail pages so they share one look. Pass tabs and (optionally) actions; every visual is overridable via className props.',
	},
];

const ComponentShowcasePage: React.FC = () => {
	const [selectedComponent, setSelectedComponent] = useState<ComponentExample>(
		componentExamples[0]
	);

	const SelectedComponent = selectedComponent.component;

	return (
		<div className="flex h-screen w-screen bg-gray-50">
			{/* Left Navigation */}
			<div className="flex h-screen w-64 flex-col bg-white shadow-sm border-r border-gray-200">
				<div className="shrink-0 p-4 border-b border-gray-200">
					<h1 className="text-xl font-bold text-gray-900">Component Showcase</h1>
					<p className="text-sm text-gray-600 mt-1">Interactive component examples</p>
				</div>

				<nav className="flex-1 overflow-y-auto p-4">
					<ul className="space-y-2">
						{componentExamples.map((example) => (
							<li key={example.name}>
								<button
									onClick={() => setSelectedComponent(example)}
									className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
										selectedComponent.name === example.name
											? "bg-brand/10 text-brand border border-brand/20"
											: "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
									}`}
								>
									{example.name}
								</button>
								{example.description && selectedComponent.name === example.name && (
									<p className="text-xs text-gray-500 mt-1 px-3">{example.description}</p>
								)}
							</li>
						))}
					</ul>
				</nav>
			</div>

			{/* Main Content */}
			<div className="flex-1 overflow-auto">
				<div className="p-6">
					<div className="mb-6">
						<h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedComponent.name}</h2>
						{selectedComponent.description && (
							<p className="text-gray-600">{selectedComponent.description}</p>
						)}
					</div>

					{/* Component Example */}
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
						<SelectedComponent />
					</div>
				</div>
			</div>
		</div>
	);
};

export default ComponentShowcasePage;
